/**
 * Deciding where the pages fall.
 *
 * Pure arithmetic, separated from the canvas so it can be tested: the bug that
 * made the first version of this unusable was a second page holding four
 * millimetres of white space, and that is a number, not a picture.
 */

/** How much a document may be shrunk to save a page, as a share of its height. */
export const SHRINK_TO_FIT = 0.09;

export interface Page {
  /** Offset into the captured image, in image pixels. */
  top: number;
  height: number;
}

export interface Plan {
  /** 1 when the document is left at full size. */
  shrink: number;
  pages: Page[];
}

/**
 * @param content   height of the captured image with trailing blank space
 *                  already removed
 * @param pageHeight one page at full size, in the same pixels
 * @param findBreak  given an ideal cut, returns a line of blank pixels at or
 *                   above it — so a page never ends halfway through a row
 */
export function planPages(
  content: number,
  pageHeight: number,
  findBreak: (ideal: number) => number = Math.floor,
): Plan {
  if (content <= 0 || pageHeight <= 0) return { shrink: 1, pages: [] };

  // A document that spills by less than the tolerance is shrunk onto one page
  // rather than dragging a nearly empty second page behind it.
  const overflow = content / pageHeight;
  const shrink = overflow > 1 && overflow <= 1 + SHRINK_TO_FIT ? pageHeight / content : 1;
  const usable = pageHeight / shrink;

  const pages: Page[] = [];
  let top = 0;
  while (top < content) {
    const remaining = content - top;
    if (remaining <= usable) {
      pages.push({ top, height: remaining });
      break;
    }
    const cut = findBreak(top + usable);
    // A break search that finds nothing useful must still make progress.
    const height = cut > top ? cut - top : Math.floor(usable);
    pages.push({ top, height });
    top += height;
  }
  return { shrink, pages };
}
